/** @format */

import { ref, watch, computed } from '@vue/composition-api'
import axiosIns from '@/libs/axios'
import { getUserData } from '@/auth/utils'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useInvoicesList(proxy, { memberOnly = false } = {}) {
  // Use toast
  const toast = useToast()

  const orderListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'id',
      label: 'ID',
      thStyle: { whiteSpace: 'nowrap' },
    },
    {
      key: 'real_name',
      label: 'Name',
      thStyle: { whiteSpace: 'nowrap' },
    },
    {
      key: 'level_id',
      label: 'Level',
      thStyle: { whiteSpace: 'nowrap' },
    },
    {
      key: 'moneys',
      label: 'Paid Commission',
      thStyle: { whiteSpace: 'nowrap' },
    },
    {
      key: 'time',
      label: 'Date',
      thStyle: { whiteSpace: 'nowrap' },
    },
  ]
  const pageSize = ref(1000) // default: load more rows; use date/member filters to narrow down
  const totalInvoices = ref(0) // total
  const currentPage = ref(1) // now Page
  const pageSizeOptions = [100, 200, 500, 1000] // pageSize Array

  const startTime = ref('')
  const endTime = ref('')
  const userId = ref('')

  const buildRequestParams = () => {
    const params = {
      pageSize: pageSize.value,
      page: currentPage.value,
    }
    if (userId.value) params.user_id = userId.value
    if (startTime.value) params.start_time = startTime.value
    if (endTime.value) params.end_time = endTime.value
    return params
  }

  const applyMemberUserId = info => {
    if (!info?.id) return null
    userId.value = String(info.id)
    localStorage.setItem('userDatas', JSON.stringify(info))
    return userId.value
  }

  const resolveMemberUserId = () => {
    try {
      const loginUser = getUserData()
      const loginUserId = applyMemberUserId(loginUser)
      if (loginUserId) return Promise.resolve(loginUserId)
    } catch (e) {
      // ignore invalid login cache
    }

    const cached = localStorage.getItem('userDatas')
    if (cached) {
      try {
        const cachedId = applyMemberUserId(JSON.parse(cached))
        if (cachedId) return Promise.resolve(cachedId)
      } catch (e) {
        // ignore invalid cache
      }
    }

    return axiosIns.get('/user/userinfo').then(res => {
      if (res.code !== 0) return null
      const info = res.data?.userInfo || res.data
      return applyMemberUserId(info)
    }).catch(() => null)
  }

  const dataMeta = computed(() => {
    const localItemsCount = orderListTable.value ? orderListTable.value.localItems.length : 0
    return {
      from: pageSize.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: pageSize.value * (currentPage.value - 1) + localItemsCount,
      of: totalInvoices.value,
    }
  })

  const refetchData = () => {
    orderListTable.value.refresh()
  }

  watch([startTime, endTime, userId], () => {
    currentPage.value = 1
  })

  watch([currentPage, pageSize, userId, startTime, endTime], () => {
    refetchData()
  })

  const fetchInvoices = (ctx, callback) => {
    const loadList = () => {
      if (memberOnly && !userId.value) {
        callback([])
        totalInvoices.value = 0
        return
      }

      const params = buildRequestParams()

      axiosIns
        .get('/user/tp-money', { params })
        .then(response => {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('[Earnings Record] GET /user/tp-money', { params, response })
          }

          if (response.code !== 0) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.msg || 'Failed to load earnings records',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
            callback([])
            totalInvoices.value = 0
            return
          }

          const payload = response.data || {}
          const list = Array.isArray(payload) ? payload : payload.list || []
          const total = payload.total ?? payload.count ?? list.length

          callback(list)
          totalInvoices.value = total
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: "Error fetching invoices' list",
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          callback([])
          totalInvoices.value = 0
        })
    }

    if (memberOnly && !userId.value) {
      resolveMemberUserId().then(id => {
        if (id) {
          loadList()
          return
        }
        callback([])
        totalInvoices.value = 0
        toast({
          component: ToastificationContent,
          props: {
            title: 'Unable to load your member profile. Please refresh and try again.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      return
    }
    loadList()
  }

  if (memberOnly) {
    resolveMemberUserId()
  }

  return {
    fetchInvoices,
    tableColumns,
    pageSize,
    currentPage,
    totalInvoices,
    dataMeta,
    pageSizeOptions,
    orderListTable,
    refetchData,
    startTime,
    endTime,
    userId,
    memberOnly,
  }
}
